import {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import secureFetch from 'shared/lib/secure-fetch';
import { UnauthorizedError } from 'shared/lib/http-errors';
import { AuthContext } from 'shared/providers/AuthProvider';
import { NotificationContext } from 'shared/providers/NotificationProvider';
import { LoadingContext } from 'shared/providers/LoadingProvider';
import { ConfigContext } from 'shared/providers/ConfigProvider';
import { ThemeModeContext } from 'shared/providers/ThemeModeContext';
import { getAdminRadCheckByUsername, searchAdminRadiusUserGroup } from 'shared/api/admin-radius';

export function useRequest() {
  const { setLoading, isLoading } = useContext(LoadingContext);
  const { getAccessToken, logout } = useContext(AuthContext);
  const { pushSuccess, pushInfo, pushError } = useContext(NotificationContext);
  const abortRef = useRef();

  // When the component that is using this unmounts abort all pending requests.
  useEffect(() => {
    abortRef.current = new AbortController();
    return () => abortRef.current.abort();
  }, []);

  const request = useCallback(async (
    serviceConfig,
    successMessage,
    cancelPrevious,
    failMessage,
  ) => {
    if (cancelPrevious) {
      // Abort all requests that the component has in progress.
      // For example, SearchableSelect should only use the result of the most recent query.
      abortRef.current.abort();
      abortRef.current = new AbortController();
    }
    if (!serviceConfig) return null;
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const data = await secureFetch(
        serviceConfig.path,
        accessToken,
        {
          body: serviceConfig.body,
          method: serviceConfig.method,
          signal: abortRef.current.signal,
        },
        serviceConfig.accept,
        serviceConfig.skipBodyCheck,
      );

      if (successMessage) {
        pushSuccess(successMessage);
      }
      return data;
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        logout();
        return undefined;
      }

      if (error.name === 'AbortError') {
        // The promise was canceled, components shouldn't set state when undefined is returned.
        return undefined;
      }

      if (error.recordNotFound) {
        // Record not found errors are expected in some situations.
        // Push an info instead of forcing the user to dismiss an error.
        pushInfo(error.message);
      } if (failMessage !== undefined) {
        pushError(failMessage);
      } else {
        pushError(error?.message || 'Something went wrong');
      }

      // Make sure to explicitly check for null if setting an error state.
      return null;
    } finally {
      setLoading(false);
    }
  }, [pushSuccess, pushInfo, pushError, setLoading, getAccessToken, logout]);

  return { isLoading, request };
}

export const useNotification = () => useContext(NotificationContext);

export function useAuth() {
  return useContext(AuthContext);
}

export function useLoading() {
  return useContext(LoadingContext);
}

export function useConfig() {
  return useContext(ConfigContext);
}

// { mode, resolvedMode, setMode, toggleMode } — see ThemeModeProvider.
export function useThemeMode() {
  return useContext(ThemeModeContext);
}

// TODO: No reason for this to be a hook; move to code that is single point of usage
export const useAdminRadUserGroups = (username) => {
  const [radUserGroups, setRadUserGroups] = useState([]);
  const { request, isLoading } = useRequest();

  useEffect(() => {
    const init = async () => {
      if (!username) return;
      const res = await request(searchAdminRadiusUserGroup(`q=${username}`));
      setRadUserGroups(res.rows);
    };

    init();
  }, [request, username]);

  return { radUserGroups, setRadUserGroups, isLoading };
};

// TODO: No reason for this to be a hook; move to code that is single point of usage
export const useAdminRadCheck = (username) => {
  const [radCheck, setRadCheck] = useState();
  const { request, isLoading } = useRequest();

  useEffect(() => {
    const init = async () => {
      if (!username) return;
      const res = await request(getAdminRadCheckByUsername(username), '', false);
      setRadCheck(res);
    };

    init();
  }, [request, username]);

  return { radCheck, setRadCheck, isLoading };
};

export { default as useSafariScrollFix } from './useSafariScrollFix';
